import React, { useState } from 'react';
import { Alert, Box, Button, FormControl, FormLabel, Input, Sheet, Typography } from '@mui/joy';
import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { AdminOnboardingAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import { StaffPicker } from '../../../components/admin/StaffPicker';
import { TaskRow } from '../onboarding/TaskRow';
import { useProfileDraft } from './ProfileDrafts';
import type { StaffTask } from '../../../types/admin';

export function OrganizationTasks({ orgId }: { orgId: string }) {
  const cache = useQueryClient();
  const [title, setTitle] = useState('');
  const [owner, setOwner] = useState<number | null>(null);
  const [due, setDue] = useState('');
  const [all, setAll] = useState(false);
  const key = ['admin', 'org-tasks', orgId];
  const query = useInfiniteQuery({
    queryKey: [...key, all],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      AdminOnboardingAPI.listTasks({
        organization: orgId,
        state: all ? 'all' : 'open',
        page_size: 25,
        page: pageParam,
      }),
    getNextPageParam: (last, pages) => (last.next ? pages.length + 1 : undefined),
  });
  const tasks = query.data?.pages.flatMap((page) => page.results) ?? [];
  const refresh = () => {
    cache.invalidateQueries({ queryKey: key });
    cache.invalidateQueries({ queryKey: ['admin-orgs'] });
    cache.invalidateQueries({ queryKey: ['admin-org'] });
    cache.invalidateQueries({ queryKey: ['admin', 'task-queue'] });
    cache.invalidateQueries({ queryKey: ['admin', 'org-profile', orgId] });
    cache.invalidateQueries({ queryKey: ['admin', 'customers'] });
  };
  const create = useMutation({
    mutationFn: () =>
      AdminOnboardingAPI.createTask({
        title: title.trim(),
        organization: orgId,
        assignee: owner,
        due_at: due ? new Date(`${due}T17:00:00`).toISOString() : null,
      }),
    onSuccess: () => {
      setTitle('');
      setDue('');
      refresh();
    },
  });
  const change = useMutation({
    mutationFn: ({
      task,
      action,
      values,
    }: {
      task: StaffTask;
      action: string;
      values?: Partial<StaffTask>;
    }) =>
      action === 'done'
        ? AdminOnboardingAPI.completeTask(task.id)
        : action === 'reopen'
          ? AdminOnboardingAPI.reopenTask(task.id)
          : AdminOnboardingAPI.updateTask(task.id, values || {}),
    onSuccess: refresh,
  });
  useProfileDraft('task', Boolean(title || due), create.isPending || change.isPending);
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg', display: 'grid', gap: 1.5 }}>
        <Typography level="title-lg">Give the next step an owner</Typography>
        <FormControl required>
          <FormLabel>Follow-up task</FormLabel>
          <Input
            disabled={create.isPending}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            slotProps={{ input: { maxLength: 255 } }}
          />
        </FormControl>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          <FormControl>
            <FormLabel>Assigned to</FormLabel>
            <StaffPicker disabled={create.isPending} value={owner} onChange={setOwner} />
          </FormControl>
          <FormControl>
            <FormLabel>Due date</FormLabel>
            <Input
              disabled={create.isPending}
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </FormControl>
        </Box>
        <Button disabled={!title.trim()} loading={create.isPending} onClick={() => create.mutate()}>
          Create follow-up task
        </Button>
      </Sheet>
      {(create.error || change.error) && (
        <Alert color="danger">
          {getApiErrorMessage(create.error || change.error, 'The task could not be saved.')}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        <Typography level="title-md">
          {query.data?.pages[0]?.count ?? '—'} {all ? 'total' : 'open'} tasks
        </Typography>
        <Button variant="plain" onClick={() => setAll(!all)}>
          {all ? 'Show open only' : 'Show completed'}
        </Button>
      </Box>
      {query.isPending && <Typography role="status">Loading tasks…</Typography>}
      {query.isError && (
        <Alert color="danger" endDecorator={<Button onClick={() => query.refetch()}>Retry</Button>}>
          Could not load tasks.
        </Alert>
      )}
      {!query.isPending && !query.isError && !tasks.length && (
        <Typography>No tasks in this view. Add a follow-up above.</Typography>
      )}
      {tasks.map((task) => (
        <TaskRow
          key={task.id}
          task={task}
          isBusy={change.isPending && change.variables?.task.id === task.id}
          onComplete={() => change.mutate({ task, action: 'done' })}
          onReopen={() => change.mutate({ task, action: 'reopen' })}
          onUpdate={(values) => change.mutate({ task, action: 'update', values })}
        />
      ))}
      {query.hasNextPage && (
        <Button variant="outlined" loading={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>
          Load more tasks
        </Button>
      )}
    </Box>
  );
}
